import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { NumberInput, type NumberInputProps } from '../primitives/NumberInput';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { Reaction } from './reactions';
import { useReactions } from './useReactions';
import { withInputAddons, type InputAddonProps } from './inputAddons';
import type { RhfLayoutProps } from './rhfLayout';
import type { EditPrecisionConfig } from './editPrecision';
import RhfInputTextField from './RhfInputTextField';

export interface RhfInputNumberProps
  extends Omit<NumberInputProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'error'>,
    InputAddonProps,
    RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  formatter?: (value: string) => string;
  reactions?: Reaction[];
  editPrecision?: EditPrecisionConfig;
}

export const RhfInputNumber = React.forwardRef<HTMLInputElement, RhfInputNumberProps>(
  function RhfInputNumber(
    {
      control,
      name,
      label,
      hint,
      required = false,
      semanticId,
      disabled,
      id,
      formatter,
      reactions,
      startAddon,
      endAddon,
      controlWidth,
      editPrecision,
      ...rest
    },
    ref,
  ) {
    const controlId = id ?? name;
    const overrides = useReactions(control, reactions);

    const finalDisabled = overrides.disabled ?? disabled ?? false;
    const finalRequired = (overrides.required as boolean) ?? required;
    const finalLabel = (overrides.label as string) ?? label;

    if (overrides.visible === false) return null;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

          if (editPrecision) {
            return (
              <RhfInputTextField
                field={field}
                controlId={controlId}
                label={finalLabel}
                hint={hint}
                required={finalRequired}
                semanticId={semanticId}
                disabled={finalDisabled}
                errorText={errorText}
                controlWidth={controlWidth}
                editPrecision={editPrecision}
                inputRef={ref}
                startAddon={startAddon}
                endAddon={endAddon}
                type="text"
                inputMode="decimal"
                {...rest}
              />
            );
          }

          const displayValue =
            field.value === undefined || field.value === null ? '' : field.value;

          const input = (
            <NumberInput
              ref={composeRefs(field.ref, ref)}
              id={controlId}
              name={field.name}
              value={displayValue}
              onChange={
                formatter
                  ? (e: React.ChangeEvent<HTMLInputElement>) =>
                      field.onChange(formatter(String(e.target.value)))
                  : field.onChange
              }
              onBlur={field.onBlur}
              disabled={finalDisabled}
              error={Boolean(errorText)}
              semanticId={semanticId}
              {...rest}
            />
          );

          return (
            <FormField
              label={finalLabel}
              htmlFor={controlId}
              hint={hint}
              required={finalRequired}
              semanticId={semanticId}
              disabled={finalDisabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              {withInputAddons(input, {
                startAddon,
                endAddon,
                error: Boolean(errorText),
                disabled: finalDisabled,
              })}
            </FormField>
          );
        }}
      />
    );
  },
);

RhfInputNumber.displayName = 'RhfInputNumber';
